import type { CandidateResumeSummary } from '@entities/candidates';
import { CellSort, DataTable, EmptyRowsFallback } from '@shared/ui';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { CandidateResumeSort, CandidateResumeSortField } from '../types';
import {
  formatCandidateResumeReferences,
  formatCandidateResumeSalary,
  formatCandidateResumeValue,
} from '../utils/formatCandidateResume';
import { CandidateResumeStatusBadge } from './CandidateResumeStatusBadge';

type CandidateResumeListTableProps = {
  candidateId: number;
  records: CandidateResumeSummary[];
  totalRecords: number;
  page: number;
  pageSize: number;
  sort: CandidateResumeSort;
  isLoading: boolean;
  hasAppliedFilters: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSortChange: (
    field: CandidateResumeSortField,
    direction?: 'asc' | 'desc'
  ) => void;
};

export const CandidateResumeListTable = ({
  candidateId,
  records,
  totalRecords,
  page,
  pageSize,
  sort,
  isLoading,
  hasAppliedFilters,
  onPageChange,
  onPageSizeChange,
  onSortChange,
}: CandidateResumeListTableProps) => {
  const navigate = useNavigate();
  const columns = useMemo(
    () => [
      {
        accessor: 'desiredJob',
        title: (
          <CellSort
            label='Желаемая работа'
            currentDirection={
              sort?.field === 'desiredJob' ? sort.direction : undefined
            }
            onSort={direction => onSortChange('desiredJob', direction)}
          />
        ),
        render: (record: CandidateResumeSummary) =>
          formatCandidateResumeValue(record.desiredJob ?? ''),
      },
      {
        accessor: 'salary',
        title: (
          <CellSort
            label='Уровень дохода от'
            currentDirection={
              sort?.field === 'salary' ? sort.direction : undefined
            }
            onSort={direction => onSortChange('salary', direction)}
          />
        ),
        render: formatCandidateResumeSalary,
      },
      {
        accessor: 'scheduleWorks',
        title: (
          <CellSort
            label='График работы'
            currentDirection={
              sort?.field === 'scheduleWorks' ? sort.direction : undefined
            }
            onSort={direction => onSortChange('scheduleWorks', direction)}
          />
        ),
        render: (record: CandidateResumeSummary) =>
          formatCandidateResumeReferences(record.scheduleWorks),
      },
      {
        accessor: 'employmentTypes',
        title: (
          <CellSort
            label='Тип занятости'
            currentDirection={
              sort?.field === 'employmentTypes' ? sort.direction : undefined
            }
            onSort={direction => onSortChange('employmentTypes', direction)}
          />
        ),
        render: (record: CandidateResumeSummary) =>
          formatCandidateResumeReferences(record.employmentTypes),
      },
      {
        accessor: 'is_active',
        title: 'Статус',
        render: (record: CandidateResumeSummary) => (
          <CandidateResumeStatusBadge isActive={record.is_active} />
        ),
      },
    ],
    [onSortChange, sort]
  );

  const emptyState = hasAppliedFilters ? (
    <EmptyRowsFallback />
  ) : (
    <EmptyRowsFallback
      desc='Список профилей пуст'
      subDesc='Профили кандидата отобразятся после их создания.'
    />
  );

  return (
    <DataTable<CandidateResumeSummary>
      fetching={isLoading}
      columns={columns}
      records={records}
      onRowClick={({ record }) =>
        navigate(`/candidate-list/${candidateId}/resume/${record.resumeId}`)
      }
      page={page}
      setPage={onPageChange}
      pageSize={pageSize}
      setPageSize={onPageSizeChange}
      totalRecords={totalRecords}
      emptyState={emptyState}
      noRecordsText=''
      highlightOnHover
      idAccessor='resumeId'
    />
  );
};
